import { css } from "@emotion/react"
import { useContext, useCallback, Fragment, use } from "react"
import { browser } from "react-dom"
import PropTypes from "prop-types"
import { camelCase, get as dig } from "lodash"
import { useSuspenseQuery } from "@tanstack/react-query"
import { Navigate, replace, useLoaderData, useParams } from "react-router"
import { DRAFT_MODE_KEY, WebBootContext } from "source/publishing/WebBoot"
import { useLayoutWidth } from "source/Layout"
import { LAYOUT_WIDTHS } from "source/useRouteLayoutWidth"
import { RoutingAwareDangerousHTML } from "source/shared/components"
import OpenGraphTags from "source/shared/OpenGraphTags"
import { useQRCode } from "source/shared/QRCode"
import { Announcements } from "./blocks/Announcements"
import { CalendarEvents } from "./blocks/CalendarEvents"
import { Registrations } from "./blocks/Registrations"
import { Sermons } from "./blocks/Sermons"
import { Podcast } from "./blocks/Podcast"
import { useJolt } from "source/shared/hooks/useJolt"
import { icons } from "source/shared/components/Icon"
import { queryClient } from "source/ReactQueryProvider"
import { pageQuery } from "./pageQuery"

export const handle = { layoutWidth: LAYOUT_WIDTHS.fullscreen }

export async function clientLoader({ params }) {
  const draftMode = window.localStorage.getItem(DRAFT_MODE_KEY) === "true"
  const version = draftMode ? "draft" : "published"
  const page = await queryClient.ensureQueryData(
    pageQuery(params.slug, version),
  )

  const redirectUri = page.data.attributes.redirect_uri
  if (redirectUri) throw replace(redirectUri)

  return null
}

export default function CustomPage() {
  const { slug } = useParams()
  const { draftMode, currentOrganization } = useContext(WebBootContext)
  const version = draftMode ? "draft" : "published"
  const loaderData = useLoaderData()

  if (loaderData?.browserOnly) {
    use(browser("No published version; draft mode is browser-only."))
  }

  const { data: page, refetch: reloadPage } = useSuspenseQuery({
    ...pageQuery(slug, version),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const conditionallyReloadPage = useCallback(
    (event) => {
      const data = event.data
      const prevPage = { slug, version }
      const nextPage = {
        slug: dig(data, "data.relationships.slug.data.id"),
        version: dig(data, "data.relationships.version.data.id"),
      }
      if (willBeOnTopOfVersionStack({ prevPage, nextPage })) reloadPage()
    },
    [reloadPage, slug, version],
  )

  useJolt(
    `/publishing/v2/jolt_subscribe`,
    `organization-${currentOrganization.id}.publishing.church_center.v2.events.page`,
    "publishing.church_center.v2.events.page.created",
    conditionallyReloadPage,
  )

  const redirectUri = page.data.attributes.redirect_uri
  if (redirectUri) {
    use(browser("Draft mode, which may clear the redirect, is browser-only."))
    return <Navigate to={redirectUri} replace />
  }

  return <PageLayout page={page.data} />
}

WebBootedHomePage.propTypes = {
  page: PropTypes.shape({
    attributes: PropTypes.shape({
      redirect_uri: PropTypes.string,
    }),
  }),
  fallback: PropTypes.node.isRequired,
}

export function WebBootedHomePage({ page, fallback }) {
  return page && !page.attributes.redirect_uri ? (
    <PageLayout page={page} />
  ) : (
    fallback
  )
}

PageLayout.propTypes = {
  page: PropTypes.object.isRequired,
}
function PageLayout({ page }) {
  const { id: pageId, attributes } = page
  const { title, content, blocks, header_image: headerImage, slug } = attributes
  const hasBlocks = blocks?.length > 0

  useLayoutWidth(hasBlocks ? LAYOUT_WIDTHS.fullscreen : LAYOUT_WIDTHS.narrow)
  useQRCode(slug)

  return (
    <div css={styles.pageLayout}>
      <OpenGraphTags
        title={title}
        imageUrl={headerImage?.attributes?.variants?.small}
      />
      {headerImage && (
        <div css={styles.headerImageWrapper}>
          <img
            alt="Page header"
            css={styles.headerImage}
            src={headerImage.attributes.variants.small}
            srcSet={`${headerImage.attributes.variants.small} 640w, ${headerImage.attributes.variants.medium} 960w, ${headerImage.attributes.variants.large} 2000w`}
          />
        </div>
      )}
      {hasBlocks ? (
        <PageBlocks blocks={blocks} pageId={pageId} />
      ) : (
        <RoutingAwareDangerousHTML html={content} />
      )}
    </div>
  )
}

PageBlocks.propTypes = {
  blocks: PropTypes.array.isRequired,
  pageId: PropTypes.string.isRequired,
}
function PageBlocks({ blocks, pageId }) {
  const reactiveBlockMap = {
    Announcements: Announcements,
    CalendarEvents: CalendarEvents,
    Registrations: Registrations,
    Sermon: Sermons,
    Podcast: Podcast,
  }

  function renderBlock(block) {
    const Component = reactiveBlockMap[block.type]
    return Component ? <Component pageId={pageId} {...block} /> : rawHtml(block)
  }

  function rawHtml(block) {
    const processedHtml = replaceServerIconPaths(block.attributes.to_html)
    return (
      <RoutingAwareDangerousHTML html={processedHtml} className="page-block" />
    )
  }

  return blocks.map((b) => <Fragment key={b.id}>{renderBlock(b)}</Fragment>)
}

export function replaceServerIconPaths(html) {
  return html.replace(
    /xlink:href="[^"]*\/@planningcenter\/icons\/sprites\/(.+?)-[a-f0-9]{16,}\.svg#([^"]+)"/g,
    (match, collection, symbol) => {
      const localPath = icons[collection] ?? icons[camelCase(collection)]
      if (localPath) {
        return `xlink:href="${localPath}#${symbol}"`
      }
      return match
    },
  )
}

function willBeOnTopOfVersionStack({ prevPage, nextPage }) {
  if (prevPage.slug === nextPage.slug) {
    switch (prevPage.version) {
      case "draft":
        return ["draft", "published"].includes(nextPage.version)
      case "published":
        return nextPage.version === "published"
    }
  }

  return false
}

const styles = {
  pageLayout: css`
    .page-block:not(:has([data-section-header]))
      + .page-block:has([data-section-header]) {
      padding-top: 1.5em;

      @media screen and (min-width: 720px) {
        padding-top: 2em;
      }
    }

    [data-giving] {
      --heading--margin-top: 0;
    }
  `,
  headerImageWrapper: css`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 auto 24px;
    max-width: 640px;

    @media screen and (max-width: 639px) {
      margin: -24px -24px 24px;
    }
  `,
  headerImage: css`
    display: block;
    width: 100%;
  `,
}
